import { UseQueryResult } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { parseUser } from '../../models/User'
import useQueryParameters, { queryParameters } from '../../utilities/useQueryParameters'
import * as api from '../api'
import { useUsers } from '../entities'
import { useApiQuery } from '../useApiQuery'
import { userActions } from '../store'
import { ReturnsGroups, ReturnsUsers } from '../useInvalidate'

const userFilterQueryParameters = queryParameters().number('page').string('usersearch')

export default function useUserFilter() {
  const dispatch = useDispatch()

  const { page, usersearch, set } = useQueryParameters(userFilterQueryParameters)
  const setPage = useCallback((page: number) => set({ page }, { preventScrollReset: true }), [set])
  const setSearch = useCallback(
    (search: string) => set({ usersearch: search, page: 0 }, { preventScrollReset: true }),
    [set]
  )

  const result: UseQueryResult<{ pageCount: number; pageSize: number; itemCount: number; uuids: string[] }> =
    useApiQuery({
      queryKey: ['userFilter', ReturnsUsers, ReturnsGroups, usersearch, page],
      placeholderData: (previousData) =>
        previousData || {
          pageCount: 1,
          pageSize: 15,
          itemCount: 8,
          uuids: ['dummy-1', 'dummy-2', 'dummy-3', 'dummy-4', 'dummy-5', 'dummy-6', 'dummy-7', 'dummy-8'],
        },
      queryFn: async () => {
        const res = await api.get(`user?page=${page + 1}&page_size=15&search=${encodeURIComponent(usersearch || '')}`)
        const users = parseUser.array(res.data)
        const pageCount = res.lastPage || 0
        const itemCount = res.total || 0
        dispatch(userActions.add(users))
        return { pageCount, pageSize: 15, itemCount, uuids: users.map(({ uuid }) => uuid) }
      },
    })

  const { users } = useUsers(result.data?.uuids || [])

  return {
    page,
    setPage,
    search: usersearch,
    setSearch,
    users,
    pageCount: result.data?.pageCount || 0,
    pageSize: result.data?.pageSize || 15,
    itemCount: result.data?.itemCount || 0,
    result,
  }
}
